import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'

import {
  getCustomer,
  getCustomerExposure,
  getCustomerMediaBlob,
  listCustomers,
  listVerificationOverrides,
  type CustomerListQuery,
} from '@/api/customers-api'
import type { CustomerMediaType } from '@/api/types'

import { customerKeys } from './customer-keys'

/**
 * Reads over the customer module.
 *
 * Paging and filtering are the server's job throughout: a branch's customer base does not fit in
 * a browser, and rows the caller may not see must never be removed client-side from a page that
 * already contains them.
 */

export const customerListQueryOptions = (query: CustomerListQuery) =>
  queryOptions({
    queryKey: customerKeys.list(query),
    queryFn: () => listCustomers(query),
    // Paging and typing in the search box keep the previous page on screen while the next one is
    // fetched. A table that blanks on every keystroke cannot be read while it is being filtered.
    placeholderData: keepPreviousData,
  })

export const customerQueryOptions = (customerId: string) =>
  queryOptions({
    queryKey: customerKeys.detail(customerId),
    queryFn: () => getCustomer(customerId),
  })

/**
 * Exposure on its own, for a screen that needs the cross-branch position without the whole
 * customer file — a loan application, for instance. The customer detail already carries it.
 */
export const customerExposureQueryOptions = (customerId: string) =>
  queryOptions({
    queryKey: customerKeys.exposure(customerId),
    queryFn: () => getCustomerExposure(customerId),
    // Arrears and blacklisting are the two facts a decision must not be made against stale, so
    // this one is always refetched when a screen mounts.
    staleTime: 0,
  })

/**
 * One specimen's bytes, as a blob — see `getCustomerMediaBlob` for why a 404 is the ordinary
 * "nothing captured" answer rather than a thrown error. `enabled` lets a caller hold off until a
 * tab is actually open or a dialog actually mounted, matching how the audit trail is only ever
 * read when a panel showing it is visible.
 */
export const customerMediaBlobQueryOptions = (
  customerId: string,
  mediaType: CustomerMediaType,
  enabled: boolean,
) =>
  queryOptions({
    queryKey: customerKeys.mediaBlob(customerId, mediaType),
    queryFn: () => getCustomerMediaBlob(customerId, mediaType),
    enabled,
    retry: false,
    staleTime: 60_000,
  })

/** The supervisor's override review queue — see `listVerificationOverrides`. */
export const verificationOverridesQueryOptions = (branchId: string | undefined) =>
  queryOptions({
    queryKey: customerKeys.verificationOverrides(branchId),
    queryFn: () => listVerificationOverrides(branchId),
    staleTime: 0,
  })

export const useCustomerList = (query: CustomerListQuery) => useQuery(customerListQueryOptions(query))

export const useCustomer = (customerId: string) => useQuery(customerQueryOptions(customerId))

export const useCustomerExposure = (customerId: string) =>
  useQuery(customerExposureQueryOptions(customerId))

export const useVerificationOverrides = (branchId: string | undefined) =>
  useQuery(verificationOverridesQueryOptions(branchId))
